/**
 * Deeply compares two objects for equality of their properties with primitive values.
 * For objects, it compares their properties infinitely deep.
 * Arrays are compared by their elements recursively.
 * Only properties with primitive values (string, number, boolean, null, undefined) are compared.
 *
 * Logs differences found during comparison.
 * 
 * @param a - First value to compare
 * @param b - Second value to compare
 * @returns true if all primitive properties are deeply equal, false otherwise
 */

// const debug = "areJSONObjectPropsEqual"
const debug = false

export function areJSONObjectPropsEqual(a: any, b: any, path: string = ''): boolean {
    // Helper to log differences
    function logDiff(message: string, aVal: any, bVal: any, path: string) {
        debug && console.log(debug, `Difference at "${path}": ${message}`, { a: aVal, b: bVal });
    }

    // If both are strictly equal (including primitives and references), return true
    if (a === b) return true

    // If either is null or undefined (but not both), return false
    if (a == null || b == null) {
        if (a !== b) logDiff('One is null/undefined, the other is not', a, b, path);
        return a === b
    }

    // If both are arrays, compare their elements recursively
    if (_.isArray(a) && _.isArray(b)) {
        if (a.length !== b.length) {
            logDiff(`Array length mismatch (${a.length} vs ${b.length})`, a, b, path);
            return false;
        }
        for (let i = 0; i < a.length; i++) {
            if (!areJSONObjectPropsEqual(a[i], b[i], `${path}[${i}]`)) {
                // Difference already logged in recursive call
                return false
            }
        }
        return true
    }

    // If both are plain objects, compare their properties recursively, ignoring key order
    if (_.isPlainObject(a) && _.isPlainObject(b)) {
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);

        // Instead of requiring key count to match, we only care about keys with defined values
        // So, we check for keys that exist only in one object and are not undefined

        // Check that every key in a is in b, or if missing in b, a[key] must be undefined
        for (const key of aKeys) {
            if (!b.hasOwnProperty(key)) {
                if (a[key] !== undefined) {
                    logDiff(`Key "${key}" missing in second object and value is not undefined`, a[key], undefined, path ? `${path}.${key}` : key)
                    return false
                }
                // else: skip, treat as equal
            } else {
                if (!areJSONObjectPropsEqual(a[key], b[key], path ? `${path}.${key}` : key)) {
                    // Difference already logged in recursive call
                    return false
                }
            }
        }

        // Check that every key in b is in a, or if missing in a, b[key] must be undefined
        for (const key of bKeys) {
            if (!a.hasOwnProperty(key)) {
                if (b[key] !== undefined) {
                    logDiff(`Key "${key}" missing in first object and value is not undefined`, undefined, b[key], path ? `${path}.${key}` : key);
                    return false;
                }
                // else: skip, treat as equal
            }
            // value comparison already done above
        }
        return true
    }

    // If both are primitive values (string, number, boolean, null, undefined), compare directly
    const isPrimitive = (val: any) =>
        val === null ||
        val === undefined ||
        typeof val === 'string' ||
        typeof val === 'number' ||
        typeof val === 'boolean'

    if (isPrimitive(a) && isPrimitive(b)) {
        if (a !== b) {
            logDiff('Primitive value mismatch', a, b, path)
        }
        return a === b
    }

    // For all other types (functions, symbols, mismatched types, etc.), return false
    logDiff('Type mismatch or unsupported type', a, b, path);
    return false;
}